import { useState, useEffect, type FormEvent } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Button, InputText, InputPassword, FloatingField } from '../../components/aip';

// スタイルをインポート
import '../../styles/aip/index.css';

const API_BASE_URL = '/api/auth';

interface FieldErrors {
  admin_name?: string;
  company_name?: string;
  password?: string;
  password_confirm?: string;
}

/**
 * 本登録ページコンポーネント
 * トークンを検証し、ユーザー情報を入力して本登録を完了する
 */
export const RegisterCompletePage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';

  // 状態管理
  const [isValidating, setIsValidating] = useState(true);
  const [isTokenValid, setIsTokenValid] = useState(false);
  const [tokenError, setTokenError] = useState<string | null>(null);
  const [email, setEmail] = useState('');

  // フォーム状態
  const [adminName, setAdminName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  // トークン検証
  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setTokenError('URLに誤りがあります');
        setIsValidating(false);
        return;
      }

      try {
        const response = await fetch(
          `${API_BASE_URL}/register/validate?token=${encodeURIComponent(token)}`
        );
        const data = await response.json();

        if (!response.ok) {
          setTokenError(data.message || '無効な登録URLです');
          setIsTokenValid(false);
        } else {
          setIsTokenValid(true);
          setEmail(data.email || '');
        }
      } catch {
        setTokenError('トークンの検証に失敗しました');
        setIsTokenValid(false);
      } finally {
        setIsValidating(false);
      }
    };

    validateToken();
  }, [token]);

  /**
   * パスワードのバリデーション
   */
  const validatePassword = (pwd: string): string | null => {
    if (pwd.length < 8) {
      return 'パスワードは8文字以上にしてください';
    }
    if (!/[a-z]/.test(pwd)) {
      return 'パスワードには英小文字を含めてください';
    }
    if (!/[A-Z]/.test(pwd)) {
      return 'パスワードには英大文字を含めてください';
    }
    if (!/\d/.test(pwd)) {
      return 'パスワードには数字を含めてください';
    }
    if (!/[!"#$%&'()*+,.\/:;<=>?@\[\\\]^_`{|}~\-]/.test(pwd)) {
      return 'パスワードには記号を含めてください';
    }
    return null;
  };

  /**
   * フォームのバリデーション
   */
  const validateForm = (): boolean => {
    const errors: FieldErrors = {};

    if (!adminName.trim()) {
      errors.admin_name = '名前を入力してください';
    } else if (adminName.length > 32) {
      errors.admin_name = '名前は32文字以内で入力してください';
    }

    if (companyName && companyName.length > 32) {
      errors.company_name = '法人名は32文字以内で入力してください';
    }

    if (!password) {
      errors.password = 'パスワードを入力してください';
    } else {
      const pwdError = validatePassword(password);
      if (pwdError) {
        errors.password = pwdError;
      }
    }

    if (!passwordConfirm) {
      errors.password_confirm = '確認用パスワードを入力してください';
    } else if (password !== passwordConfirm) {
      errors.password_confirm = '確認用パスワードが一致しません';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length > 0;
  };

  /**
   * フィールドのエラーをクリア
   */
  const clearFieldError = (field: keyof FieldErrors) => {
    if (fieldErrors[field]) {
      setFieldErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/register/complete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token,
          admin_name: adminName.trim(),
          company_name: companyName.trim() || undefined,
          password,
          password_confirm: passwordConfirm,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        // バリデーションエラーの場合
        if (data.status === 'validation_error' && data.validation_errors) {
          setFieldErrors(data.validation_errors);
          return;
        }
        throw new Error(data.message || '登録に失敗しました');
      }

      setIsSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : '登録に失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  // ローディング中
  if (isValidating) {
    return (
      <div className="aip-html-body">
        <div className="aip-main" data-size="xs">
          <header className="aip-main__header">
            <div className="aip-main__header-container">
              <h1 className="aip-main__header-title">AiiPo Management</h1>
            </div>
          </header>

          <div className="aip-main__content">
            <div style={{ textAlign: 'center', padding: '3rem 0' }}>
              <div
                style={{
                  width: '40px',
                  height: '40px',
                  border: '3px solid var(--color-gray200, #e0e0e0)',
                  borderTopColor: 'var(--color-primary, #0035c3)',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                  margin: '0 auto 1rem',
                }}
              />
              <p style={{ color: 'var(--color-text-secondary, #666)' }}>
                確認中...
              </p>
              <style>{`
                @keyframes spin {
                  to { transform: rotate(360deg); }
                }
              `}</style>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // トークンエラー
  if (!isTokenValid) {
    return (
      <div className="aip-html-body">
        <div className="aip-main" data-size="xs">
          <header className="aip-main__header">
            <div className="aip-main__header-container">
              <h1 className="aip-main__header-title">AiiPo Management</h1>
            </div>
          </header>

          <div className="aip-main__content">
            <div style={{ textAlign: 'center', padding: '2rem 0' }}>
              <div
                style={{
                  width: '64px',
                  height: '64px',
                  borderRadius: '50%',
                  backgroundColor: 'var(--color-red50, #ffebee)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 1.5rem',
                }}
              >
                <svg
                  width="32"
                  height="32"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="var(--color-alert, #e74c3c)"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="10" />
                  <line x1="15" y1="9" x2="9" y2="15" />
                  <line x1="9" y1="9" x2="15" y2="15" />
                </svg>
              </div>

              <h2
                style={{
                  fontSize: '1.25rem',
                  fontWeight: 600,
                  marginBottom: '1rem',
                  color: 'var(--color-text-primary, #333)',
                }}
              >
                登録URLが無効です
              </h2>

              <p
                style={{
                  fontSize: '0.9375rem',
                  color: 'var(--color-text-secondary, #666)',
                  lineHeight: 1.6,
                  marginBottom: '2rem',
                }}
              >
                {tokenError}
              </p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', alignItems: 'center' }}>
                <Link to="/meeting/manage-new/register">
                  <Button buttonType="solid-fill" size="md">
                    新規登録をやり直す
                  </Button>
                </Link>
                <Link to="/meeting/manage-new/login" className="aip-link">
                  ログインページへ戻る
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 成功時の表示
  if (isSuccess) {
    return (
      <div className="aip-html-body">
        <div className="aip-main" data-size="xs">
          <header className="aip-main__header">
            <div className="aip-main__header-container">
              <h1 className="aip-main__header-title">AiiPo Management</h1>
            </div>
          </header>

          <div className="aip-main__content">
            <div style={{ textAlign: 'center', padding: '2rem 0' }}>
              <div
                style={{
                  width: '64px',
                  height: '64px',
                  borderRadius: '50%',
                  backgroundColor: 'var(--color-green50, #e8f5e9)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 1.5rem',
                }}
              >
                <svg
                  width="32"
                  height="32"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="var(--color-success, #27ae60)"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22,4 12,14.01 9,11.01" />
                </svg>
              </div>

              <h2
                style={{
                  fontSize: '1.25rem',
                  fontWeight: 600,
                  marginBottom: '1rem',
                  color: 'var(--color-text-primary, #333)',
                }}
              >
                会員登録が完了しました
              </h2>

              <p
                style={{
                  fontSize: '0.9375rem',
                  color: 'var(--color-text-secondary, #666)',
                  lineHeight: 1.6,
                  marginBottom: '2rem',
                }}
              >
                ご登録いただきありがとうございます。<br />
                ログインページからログインしてください。
              </p>

              <div style={{ textAlign: 'center' }}>
                <Button
                  buttonType="solid-fill"
                  size="md"
                  onClick={() => navigate('/meeting/manage-new/login')}
                >
                  ログインページへ
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="aip-html-body">
      <div className="aip-main" data-size="xs">
        <header className="aip-main__header">
          <div className="aip-main__header-container">
            <h1 className="aip-main__header-title">AiiPo Management</h1>
          </div>
        </header>

        <div className="aip-main__content">
          <h2 style={{ fontSize: '1.5rem', fontWeight: 500, marginBottom: '1rem' }}>
            本登録
          </h2>

          <p
            style={{
              fontSize: '0.9375rem',
              color: 'var(--color-text-secondary, #666)',
              marginBottom: '1.5rem',
              lineHeight: 1.6,
            }}
          >
            以下のユーザ情報を登録して、本登録を完了させてください。
          </p>

          {/* メールアドレス表示 */}
          <div
            style={{
              backgroundColor: 'var(--color-gray50, #f5f5f5)',
              padding: '0.75rem 1rem',
              borderRadius: '0.5rem',
              marginBottom: '1.5rem',
            }}
          >
            <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--color-text-secondary, #666)' }}>
              メールアドレス
            </p>
            <p style={{ margin: '0.25rem 0 0', fontSize: '1rem', fontWeight: 500 }}>
              {email}
            </p>
          </div>

          {error && (
            <div
              style={{
                padding: '0.75rem 1rem',
                backgroundColor: 'var(--color-red50)',
                color: 'var(--color-alert)',
                borderRadius: '0.5rem',
                marginBottom: '1rem',
                fontSize: '0.875rem',
              }}
            >
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate>
            <div className="aip-form-container__body">
              {/* 名前 */}
              <FloatingField
                label="名前"
                htmlFor="admin_name"
                column="2"
                required
                errorText={fieldErrors.admin_name}
                hasError={!!fieldErrors.admin_name}
              >
                <InputText
                  id="admin_name"
                  name="admin_name"
                  placeholder="山田 太郎"
                  value={adminName}
                  onChange={(e) => {
                    setAdminName(e.target.value);
                    clearFieldError('admin_name');
                  }}
                  autoComplete="name"
                  className="aip-floating-field__control"
                  aria-invalid={!!fieldErrors.admin_name}
                />
              </FloatingField>

              {/* 法人名 */}
              <FloatingField
                label="法人名"
                htmlFor="company_name"
                column="2"
                supportText="任意"
                errorText={fieldErrors.company_name}
                hasError={!!fieldErrors.company_name}
              >
                <InputText
                  id="company_name"
                  name="company_name"
                  placeholder="株式会社サンプル"
                  value={companyName}
                  onChange={(e) => {
                    setCompanyName(e.target.value);
                    clearFieldError('company_name');
                  }}
                  autoComplete="organization"
                  className="aip-floating-field__control"
                  aria-invalid={!!fieldErrors.company_name}
                />
              </FloatingField>

              {/* パスワード */}
              <FloatingField
                label="パスワード"
                htmlFor="password"
                column="2"
                required
                supportText="8文字以上、英大文字・小文字・数字・記号を含む"
                errorText={fieldErrors.password}
                hasError={!!fieldErrors.password}
              >
                <InputPassword
                  id="password"
                  name="password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    clearFieldError('password');
                  }}
                  autoComplete="new-password"
                  aria-invalid={!!fieldErrors.password}
                />
              </FloatingField>

              {/* パスワード確認 */}
              <FloatingField
                label="パスワード（確認）"
                htmlFor="password_confirm"
                column="2"
                required
                errorText={fieldErrors.password_confirm}
                hasError={!!fieldErrors.password_confirm}
              >
                <InputPassword
                  id="password_confirm"
                  name="password_confirm"
                  value={passwordConfirm}
                  onChange={(e) => {
                    setPasswordConfirm(e.target.value);
                    clearFieldError('password_confirm');
                  }}
                  autoComplete="new-password"
                  aria-invalid={!!fieldErrors.password_confirm}
                />
              </FloatingField>

              {/* 送信ボタン */}
              <div data-column="2" style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <Button
                  buttonType="solid-fill"
                  size="md"
                  type="submit"
                  disabled={isLoading}
                  style={{ width: '100%' }}
                >
                  {isLoading ? '登録中...' : '登録を完了する'}
                </Button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default RegisterCompletePage;

